/**
 * Pure-data English catalogue for namespace 'ease' (imports only core/util/types).
 * Keys are written WITHOUT the namespace prefix; core produces MessageRefs such as 'ease.noBezierOnSpeed'.
 *
 * Groups:
 *  - guard reasons produced by `Ease.validateFor` (src/core/ease/guards.ts, EASE_REASON);
 *  - catalogue words for the easing picker and labels: family names, plain strength words (grid rows), shape
 *    words (grid columns), variant names and the notes attached to catalogue entries.
 */

import type { MessageCatalog } from '../util/types';

const catalog: MessageCatalog = {
  ns: 'ease',
  locale: 'en',
  messages: {
    // Guard reasons (validateFor)
    unknownEasing: 'Unknown curve number {id}. RPE curves are numbered 1 to 29.',
    sliceOutOfRange: 'The part of the curve to use must lie between 0 and 1.',
    sliceEmpty: 'The start of the curve part must come before its end.',
    sliceDegenerate:
      'This part of the curve starts and ends at the same height, so Phira cannot stretch it and would show nothing. Choose a different part.',
    bezierNotFinite: 'The custom curve has a handle without a valid position.',
    bezierXOutOfRange: 'The handles of a custom curve must stay between 0 and 1 horizontally.',
    noBezierOnSpeed: 'Scroll speed cannot use a custom curve: Phira would crash. Use a numbered curve instead.',
    speedGuidedLinearOnly:
      'Phira plays speed changes as straight ramps. Choose Linear or Stay, or switch to the Full level to use other curves (they are baked on export).',
    textRevealOvershoot:
      'Phira crashes when typing or erasing text with a curve that goes past its start or end. Choose a curve that stays in range.',
    controlNoBezier: 'Note controls only support the numbered RPE curves, not custom curves.',
    controlNoHold: 'Note controls cannot stay and jump. Choose a numbered RPE curve.',
    controlNoSlice: 'Note controls always use the whole curve. Remove the partial range.',
    extraVarNoBezier:
      'Effect and video values cannot use a custom curve: extra.json has no custom curves. Bake the motion instead.',

    // Catalogue words
    'name.linear': 'Linear',
    'name.hold': 'Stay, then jump',
    'name.bezier': 'Custom curve',
    'family.linear': 'Linear',
    'family.sine': 'Sine',
    'family.quad': 'Quad',
    'family.cubic': 'Cubic',
    'family.quart': 'Quart',
    'family.quint': 'Quint',
    'family.expo': 'Expo',
    'family.circ': 'Circ',
    'family.back': 'Back',
    'family.elastic': 'Elastic',
    'family.bounce': 'Bounce',
    'strength.gentle': 'Gentle',
    'strength.soft': 'Soft',
    'strength.medium': 'Medium',
    'strength.strong': 'Strong',
    'strength.stronger': 'Stronger',
    'strength.extreme': 'Extreme',
    'strength.sharp': 'Sharp',
    'strength.overshoot': 'Overshoot',
    'strength.springy': 'Springy',
    'strength.bouncy': 'Bouncy',
    'shape.slowStart': 'Slow start',
    'shape.slowEnd': 'Slow end',
    'shape.slowBoth': 'Slow both',
    'variant.in': 'In',
    'variant.out': 'Out',
    'variant.inout': 'In and out',
    'note.differsInRpe': 'Phira draws this curve differently from RPE.',
    'note.overshoots': 'This curve goes past its start or end value.',
    'note.approxBezier':
      'RPE has no number for this curve: it is offered as a fitted custom curve (largest difference {error} %).',
    'note.splitApprox': 'Splitting keeps the motion within {percent} % of the original.',
    'note.splitBake': 'Part of this segment cannot be split exactly and will be baked into straight pieces.',
  },
};

export default catalog;
